import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { api } from '../../lib/api';
import { getToken } from '../../lib/auth';
import { toAbsoluteApiMediaUrl } from '../../lib/mediaUrl';


interface User {
  role: 'admin' | 'teacher' | 'student' | 'parent' | 'secretariat';
  name: string;
  avatar?: string;
  is_platform_admin?: boolean;
}

interface HeaderProps {
  user: User;
  notificationCount?: number;
  onNavigate?: (path: string) => void;
  onSignOut?: () => void;
}

const Header = ({ user, notificationCount = 0, onNavigate, onSignOut }: HeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState<number>(notificationCount);
  const [isUnder16Student, setIsUnder16Student] = useState(false);
  const [planTag, setPlanTag] = useState<{ text: string; kind: 'free' | 'basic' | 'full' | 'license' }>({
    text: 'FREE',
    kind: 'free',
  });

  const refreshUnreadNotifications = async () => {
    const token = getToken();
    if (!token) {
      setUnreadNotifications(0);
      return;
    }
    try {
      const resp = await api.get<{ unread: number }>('/api/notificari/notifications/unread-count');
      const unread = Number((resp.data as any)?.unread ?? 0);
      setUnreadNotifications(Number.isFinite(unread) ? unread : 0);
    } catch {
      setUnreadNotifications(0);
    }
  };

  const refreshPlanTag = async () => {
    const token = getToken();
    if (!token) {
      setPlanTag({ text: 'FREE', kind: 'free' });
      return;
    }
    try {
      const resp = await api.get('/api/subscriptions/me');
      const plan = String((resp.data as any)?.plan || 'free').toLowerCase();
      const unrestricted = Boolean((resp.data as any)?.unrestricted);
      if (unrestricted || plan === 'unrestricted') {
        setPlanTag({ text: 'LICENȚĂ', kind: 'license' });
        return;
      }
      if (plan === 'basic') {
        setPlanTag({ text: 'BASIC', kind: 'basic' });
        return;
      }
      if (plan === 'full') {
        setPlanTag({ text: 'FULL', kind: 'full' });
        return;
      }
      setPlanTag({ text: 'FREE', kind: 'free' });
    } catch {
      setPlanTag({ text: 'FREE', kind: 'free' });
    }
  };

  const planTagClass = (kind: typeof planTag.kind) => {
    if (kind === 'license') return 'border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300';
    if (kind === 'full') return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
    if (kind === 'basic') return 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300';
    return 'border-border bg-muted/40 text-muted-foreground';
  };

  const roleLabel = (role: User['role']) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'teacher':
        return 'Profesor';
      case 'student':
        return 'Elev';
      case 'parent':
        return 'Părinte';
      case 'secretariat':
        return 'Secretariat';
      default:
        return role;
    }
  };

  const navigationItems = {
    admin: [
      { label: 'Panou', path: '/platform-admin-dashboard', icon: 'LayoutDashboard' },
      { label: 'Calendar', path: '/calendar', icon: 'CalendarDays' },
      { label: 'Comunicare', path: '/communication-center', icon: 'MessageSquare' },
      { label: 'Comunitate', path: '/school-social-platform', icon: 'Users' },
      { label: 'Bibliotecă', path: '/library', icon: 'Library' },
    ],
    secretariat: [
      { label: 'Panou', path: '/secretariat-dashboard', icon: 'LayoutDashboard' },
      { label: 'Calendar', path: '/calendar', icon: 'CalendarDays' },
      { label: 'Comunicare', path: '/communication-center', icon: 'MessageSquare' },
      { label: 'Comunitate', path: '/school-social-platform', icon: 'Users' },
      { label: 'Documente', path: '/documents', icon: 'FileText' },
      { label: 'Bibliotecă', path: '/library', icon: 'Library' },
    ],
    teacher: [
      { label: 'Panou', path: '/teacher-dashboard', icon: 'LayoutDashboard' },
      { label: 'Gestionare clase', path: '/class-management-hub', icon: 'School' },
      { label: 'Catalog', path: '/gradebook-interface', icon: 'BookOpen' },
      { label: 'Clasă online', path: '/online-classroom', icon: 'Video' },
      { label: 'Calendar', path: '/calendar', icon: 'CalendarDays' },
      { label: 'Comunicare', path: '/communication-center', icon: 'MessageSquare' },
      { label: 'Comunitate', path: '/school-social-platform', icon: 'Users' },
      { label: 'Bibliotecă', path: '/library', icon: 'Library' },
    ],
    student: [
      { label: 'Panou', path: '/student-dashboard', icon: 'LayoutDashboard' },
      { label: 'Clasă online', path: '/online-classroom', icon: 'Video' },
      { label: 'Calendar', path: '/calendar', icon: 'CalendarDays' },
      { label: 'Comunicare', path: '/communication-center', icon: 'MessageSquare' },
      { label: 'Comunitate', path: '/school-social-platform', icon: 'Users' },
      { label: 'Bibliotecă', path: '/library', icon: 'Library' },
    ],
    parent: [
      { label: 'Panou', path: '/parent-dashboard', icon: 'LayoutDashboard' },
      { label: 'Calendar', path: '/calendar', icon: 'CalendarDays' },
      { label: 'Comunicare', path: '/communication-center', icon: 'MessageSquare' },
      { label: 'Comunitate', path: '/school-social-platform', icon: 'Users' },
      { label: 'Bibliotecă', path: '/library', icon: 'Library' },
    ],
  };

  const currentNavItems = (navigationItems[user.role] || []).filter((item) => {
    if (item.label === 'Catalog') return false;
    if (user.role === 'admin' && user.is_platform_admin && item.path === '/class-management-hub') return false;
    if (user.role === 'admin' && user.is_platform_admin && item.label === 'Gestionare clase') return false;
    return true;
  });

  const handleNavigation = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
    setIsMobileMenuOpen(false);
  };

  const openOverlay = (path: string) => {
    // Open as modal overlay (like footer legal/contact pages).
    navigate(path, { state: { backgroundLocation: location } });
    setIsMobileMenuOpen(false);
    setIsProfileMenuOpen(false);
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.profile-menu-container')) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    void refreshUnreadNotifications();
    const t = window.setInterval(() => void refreshUnreadNotifications(), 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void refreshUnreadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    void refreshPlanTag();
    const t = window.setInterval(() => void refreshPlanTag(), 60000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void refreshPlanTag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const refreshUnder16 = async () => {
      if (user.role !== 'student') {
        setIsUnder16Student(false);
        return;
      }
      const token = getToken();
      if (!token) {
        setIsUnder16Student(false);
        return;
      }
      try {
        const resp = await api.get('/api/users/me');
        const over16 = Boolean((resp.data as any)?.over16);
        if (!cancelled) setIsUnder16Student(!over16);
      } catch {
        if (!cancelled) setIsUnder16Student(false);
      }
    };

    void refreshUnder16();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role]);

  return (
    <header className="fixed top-0 left-0 right-0 z-[1000] bg-card border-b border-border shadow-sm">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        <div className="flex items-center gap-6">
          <button
            onClick={() => handleNavigation(currentNavItems[0]?.path || '/')}
            className="flex items-center gap-2 transition-opacity duration-150 hover:opacity-80"
            aria-label="Acasă"
          >
            <div className="flex items-center justify-center w-12 h-12 rounded-md">
              <img src="/assets/scola-logo.png" alt="Scola" className="w-10 h-10 object-contain" />
            </div>
            <span
              className="hidden text-xl font-extrabold tracking-tight sm:block bg-gradient-to-r from-blue-700 via-sky-500 to-green-500 bg-clip-text text-transparent"
              style={{ fontFamily: 'Sora, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}
            >
              Scola
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Navigare principală">
            {currentNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                  isActivePath(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                aria-current={isActivePath(item.path) ? 'page' : undefined}
              >
                <Icon name={item.icon} size={18} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setUnreadNotifications(0);
              handleNavigation('/notifications');
            }}
            className="relative p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
            aria-label={`Notificări${unreadNotifications > 0 ? ` (${unreadNotifications} necitite)` : ''}`}
          >
            <Icon name="Bell" size={20} />
            {unreadNotifications > 0 && (
              <span className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-error rounded-full">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
	          </button>

	          <button
	            onClick={isUnder16Student ? undefined : () => handleNavigation('/subscriptions')}
	            className={`hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors duration-150 ${
	              isUnder16Student ? 'cursor-default opacity-90' : 'hover:bg-muted'
	            } ${planTagClass(planTag.kind)}`}
	            aria-label={isUnder16Student ? `Plan abonament: ${planTag.text} (gestionat de părinte)` : `Plan abonament: ${planTag.text}`}
	            title={isUnder16Student ? `Plan abonament: ${planTag.text} (gestionat de părinte)` : `Plan abonament: ${planTag.text}`}
	          >
	            <Icon name={planTag.kind === 'license' ? 'BadgeCheck' : 'CreditCard'} size={14} />
	            <span>{planTag.text}</span>
	          </button>

	          <button
	            onClick={() => openOverlay('/contact')}
	            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
	            aria-label="Ajutor"
	            title="Ajutor"
	          >
	            <Icon name="CircleHelp" size={20} />
	          </button>
	
	          <div className="relative profile-menu-container">
            <button
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-all duration-150"
              aria-expanded={isProfileMenuOpen}
              aria-haspopup="true"
            >
              {user.avatar ? (
                <img
                  src={toAbsoluteApiMediaUrl(user.avatar) || user.avatar}
                  alt="Profil"
                  className="w-8 h-8 rounded-full object-cover border border-border"
                />
              ) : (
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                  {user.name.charAt(0).toUpperCase()}
                </div>
              )}
              <Icon name="ChevronDown" size={16} className="hidden sm:block text-muted-foreground" />
            </button>

            {isProfileMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-md shadow-lg animate-scale-in">
                <div className="p-3 border-b border-border">
                  <p className="text-sm font-medium text-foreground">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{roleLabel(user.role)}</p>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      handleNavigation('/my-account');
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
                  >
                    <Icon name="User" size={16} />
                    <span>Contul meu</span>
                  </button>
                  {!isUnder16Student ? (
                    <button
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        handleNavigation('/subscriptions');
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
                    >
                      <Icon name="CreditCard" size={16} />
                      <span>Abonamente</span>
                    </button>
                  ) : null}
                  <button
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      if (onSignOut) {
                        onSignOut();
                      }
                      handleNavigation('/authentication-portal');
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
                  >
                    <Icon name="LogOut" size={16} />
                    <span>Deconectare</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
            aria-label="Deschide/închide meniul"
            aria-expanded={isMobileMenuOpen}
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[1010] lg:hidden animate-fade-in"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
          <nav
            className="fixed top-16 right-0 bottom-0 w-64 bg-card border-l border-border z-[1030] lg:hidden overflow-y-auto animate-slide-in-right"
            aria-label="Navigare mobilă"
          >
            <div className="p-4 space-y-1">
              {currentNavItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-md text-sm font-medium transition-all duration-150 ${
                    isActivePath(item.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                  aria-current={isActivePath(item.path) ? 'page' : undefined}
                >
                  <Icon name={item.icon} size={20} />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </nav>
        </>
      )}
    </header>
  );
};

export default Header;
